import { useEffect, useState } from 'react';

import { explat } from '@1debit/explat';
import { useSelector } from 'react-redux';

import { selectUserId, selectUserIsConnected } from 'selectors/user';

import getForcedExperimentsFromQueryParams from 'utils/getForcedExperimentsFromQueryParams';
import { getAnonymousId } from 'utils/segment';

/*
checks QSPs for force bucketing param
if present, then return variant specified in QSP 
and skip call to Explat 
*/
function getVariantByKey(key) {
  const experiments = getForcedExperimentsFromQueryParams();
  const experiment = experiments.find(
    ({ experimentKey }) => experimentKey === key
  );
  return experiment?.variantKey;
}

/*
useExplat uses the getVariant method to get 
variant value stored in cache (set by fetchAndStoreVariants)

skipLogging - Controls whether Explat should bucket the user or not. 
  Most experiments are used on a single page, so use "false" to bucket the user.
  When the experiment hook is called on multiple pages in the enrollment flow:
  - Use "false" on the exposure point page where bucketing should occur
  - Use "true" on subsequent pages to get variant without re-bucketing (ie, caches variant and skips bucketing event)

isEligible - describes whether or not a member qualifies to be bucketed into this experiment 

useAnonymousId - if true, then we'll pass in anonymousId as unitId value 
*/
export function useExplat(
  experimentName,
  { skipLogging = false, isEligible = true, useAnonymousID = false }
) {
  const getUnitId = () => {
    if (useAnonymousID) {
      return getAnonymousId();
    }
    return userId;
  };

  const userIsConnected = useSelector(selectUserIsConnected);
  const userId = useSelector(selectUserId);
  const [isLoaded, setIsLoaded] = useState(!isEligible);
  const [isOverridden, setIsOverridden] = useState(false);
  const [unitId, setUnitId] = useState(getUnitId());
  const [variant, setVariant] = useState(() => {
    const overrideVariant = getVariantByKey(experimentName);
    if (overrideVariant) {
      setIsOverridden(true);
      setIsLoaded(true);
    }
    return overrideVariant;
  });

  useEffect(() => {
    /*
    This is to re-evaluate variant for user_id based 
    experiments that rendered before user was created
    (ie, experiments that load before submitting first page)
    */
    if (userIsConnected && isLoaded && !isOverridden) {
      setIsLoaded(!isEligible);
      setUnitId(getUnitId());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsConnected, isEligible]);

  useEffect(() => {
    if (!isLoaded && !isOverridden && isEligible && unitId) {
      const variant = explat.getVariant(experimentName, unitId, skipLogging);
      setVariant(variant);
      setIsLoaded(true);
    }
  }, [experimentName, isEligible, isLoaded, isOverridden, skipLogging, unitId]);

  return {
    isLoaded,
    variant,
  };
}
